/**
 * Locked — cheeky lockout screen blaming the accountants.
 * Routes here when subscription_status is locked / past_due / canceled.
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Locked() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const startCheckout = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/billing/checkout", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not start checkout");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative bg-[var(--canvas)] overflow-hidden">
      <div className="grain" />
      <div className="max-w-[1100px] mx-auto px-6 py-16 grid md:grid-cols-12 gap-10 items-center relative">
        <div className="md:col-span-7 relative z-10">
          <p className="text-[12px] uppercase tracking-[0.22em] text-[var(--ink-muted)] mb-5" data-testid="locked-eyebrow">
            A short, awkward conversation
          </p>
          <h1 className="font-display text-5xl md:text-7xl leading-[0.95] tracking-tight" data-testid="locked-title">
            Our accountants are<br />
            <span className="italic text-[var(--brand)]">tapping their watches.</span>
          </h1>
          <p className="mt-8 text-lg text-[var(--ink-secondary)] leading-relaxed max-w-2xl" data-testid="locked-body">
            Your free trial wrapped, and they say we have to actually charge for this thing
            now. Pop a card on file and you're back in the writers' room in about ten seconds.
            $19.97/month. Cancel any time. Your work isn't going anywhere.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-3">
            <button
              onClick={startCheckout}
              disabled={loading}
              className="btn-brand text-base disabled:opacity-60"
              data-testid="locked-pay-btn"
            >
              {loading ? "Opening Stripe…" : "Add payment method — $19.97/mo"}
            </button>
            <button
              onClick={() => navigate("/app")}
              className="btn-ghost text-base"
              data-testid="locked-export-btn"
            >
              Just let me export my work
            </button>
            <button onClick={logout} className="text-sm text-[var(--ink-muted)] hover:text-[var(--ink-primary)] ml-2 underline-offset-2 hover:underline" data-testid="locked-logout">
              Sign out
            </button>
          </div>
          <p className="mt-6 text-xs text-[var(--ink-muted)]" data-testid="locked-fineprint">
            We never hold your scripts hostage. Export to PDF, .fdx, or .fountain whenever you want — paid or not.
          </p>
        </div>
        <div className="md:col-span-5 relative">
          <img
            src="https://images.unsplash.com/photo-1614890020586-8e72b96fdc39?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwxfHxpbXBhdGllbnQlMjBhY2NvdW50YW50JTIwbG9va2luZyUyMGF0JTIwd2F0Y2h8ZW58MHx8fHwxNzgwNjg0NTIwfDA&ixlib=rb-4.1.0&q=85"
            alt="Impatient accountant"
            className="rounded-md shadow-xl border border-[var(--line)] grayscale-[0.2]"
            data-testid="locked-image"
          />
          <div className="absolute -bottom-6 left-6 right-6 bg-[var(--ink-primary)] text-white text-[11px] uppercase tracking-widest px-4 py-3 rounded-md shadow-lg hidden md:block">
            * The accountants are fictional. The price is real.
          </div>
        </div>
      </div>
    </div>
  );
}
